import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Terminal, KeyRound, Copy, Check, Play, Loader2, ShieldCheck, Code2, Zap, ArrowRight, Webhook, Gauge, FileJson, Download,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { InstitutionInquiryModal } from '../components/brand/InstitutionInquiryModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPEC_URL = `${API}/v1/openapi.json`;

const ENDPOINTS = [
  {
    method: 'GET', path: '/v1/health', auth: false,
    desc: 'Liveness probe. No authentication required.',
  },
  {
    method: 'GET', path: '/v1/clinicians/search', auth: true,
    desc: 'Search the verified clinical workforce by state, city, ZIP, or diagnosis. Returns de-identified sandbox data.',
    params: 'state, city, zip, diagnosis, limit',
  },
  {
    method: 'POST', path: '/v1/discharge-referrals', auth: true,
    desc: 'Submit a discharge referral and instantly receive matched, verified clinicians.',
    body: '{ "primary_diagnosis": "CHF", "care_type": "Skilled Nursing", "zip": "78701" }',
  },
  {
    method: 'GET', path: '/v1/geo/zip/{zip}', auth: true,
    desc: 'Resolve a ZIP to its nearest service city + state.',
  },
  {
    method: 'POST', path: '/v1/webhooks', auth: true,
    desc: 'Register the endpoint that receives signed event callbacks. Returns a signing secret once.',
    body: '{ "url": "https://your-app.com/hooks", "events": ["referral.created"] }',
  },
];

const WEBHOOK_EVENTS = ['referral.created', 'referral.matched', 'ping'];

const FHIR_ENDPOINTS = [
  { method: 'GET', path: '/v1/fhir/metadata', auth: false,
    desc: 'FHIR R4 CapabilityStatement — read this to discover supported resources and search params.' },
  { method: 'GET', path: '/v1/fhir/Patient/{id}', auth: true,
    desc: 'Read a Patient resource (US-Core profile reference).',
    example: '/v1/fhir/Patient/abc-123' },
  { method: 'GET', path: '/v1/fhir/Encounter/{id}', auth: true,
    desc: 'Read an Encounter resource (HL7 v3 class=HH, home health visit).' },
  { method: 'GET', path: '/v1/fhir/Encounter', auth: true,
    desc: 'Search Encounters. Returns a Bundle (searchset).',
    example: '?patient=abc-123&status=in-progress&_count=10' },
  { method: 'GET', path: '/v1/fhir/ServiceRequest/{id}', auth: true,
    desc: 'Read a ServiceRequest representing a discharge referral.' },
  { method: 'GET', path: '/v1/fhir/ServiceRequest', auth: true,
    desc: 'Search ServiceRequests. Returns a Bundle (searchset).',
    example: '?patient=abc-123&status=active&_count=10' },
  { method: 'POST', path: '/v1/fhir/ServiceRequest', auth: true,
    desc: 'Create a discharge referral via a real FHIR R4 ServiceRequest resource. Fires the referral.created webhook.' },
];

// Minimal Postman v2.1 collection for the FHIR R4 surface. Lets a hospital
// integrator import and start hitting endpoints in 30 seconds.
const POSTMAN_COLLECTION = (api, key) => ({
  info: {
    name: 'CaraLoom · FHIR R4 (Hospital-to-Home)',
    description: 'Minimal R4 surface for discharge interoperability. Replace {{apiKey}} with your sandbox key.',
    schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
  },
  variable: [
    { key: 'baseUrl', value: api },
    { key: 'apiKey', value: key || 'clk_sandbox_YOUR_KEY' },
  ],
  item: [
    { name: 'GET CapabilityStatement (public)',
      request: { method: 'GET', header: [{ key: 'Accept', value: 'application/fhir+json' }],
        url: '{{baseUrl}}/v1/fhir/metadata' } },
    { name: 'GET Patient by id',
      request: { method: 'GET', header: [{ key: 'Authorization', value: 'Bearer {{apiKey}}' }],
        url: '{{baseUrl}}/v1/fhir/Patient/REPLACE_WITH_PATIENT_ID' } },
    { name: 'GET Encounter search (by patient)',
      request: { method: 'GET', header: [{ key: 'Authorization', value: 'Bearer {{apiKey}}' }],
        url: '{{baseUrl}}/v1/fhir/Encounter?patient=REPLACE_WITH_PATIENT_ID&_count=10' } },
    { name: 'POST ServiceRequest (create discharge referral)',
      request: { method: 'POST',
        header: [
          { key: 'Authorization', value: 'Bearer {{apiKey}}' },
          { key: 'Content-Type', value: 'application/fhir+json' },
        ],
        body: { mode: 'raw', raw: JSON.stringify({
          resourceType: 'ServiceRequest',
          status: 'active', intent: 'order',
          subject: { display: 'Jane Doe, MRN-12345' },
          priority: 'urgent',
          reasonCode: [{ coding: [{ system: 'http://hl7.org/fhir/sid/icd-10-cm',
                                     code: 'I50.9', display: 'Heart failure' }] }],
          note: [{ text: 'Needs IV diuretic monitoring at home.' }],
        }, null, 2) },
        url: '{{baseUrl}}/v1/fhir/ServiceRequest' } },
    { name: 'GET ServiceRequest search',
      request: { method: 'GET', header: [{ key: 'Authorization', value: 'Bearer {{apiKey}}' }],
        url: '{{baseUrl}}/v1/fhir/ServiceRequest?_count=10' } },
  ],
});

const CodeBlock = ({ children, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(children).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1400); }).catch(() => {});
  };
  return (
    <div className="relative group">
      <pre className="bg-cn-ink-900 text-slate-200 rounded-xl p-4 pr-12 text-xs overflow-x-auto font-mono leading-relaxed" data-testid={testid}>{children}</pre>
      <button onClick={copy} className="absolute top-2.5 right-2.5 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors" title="Copy">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
};

// Interactive Swagger UI, lazy-loaded from CDN (no bundled dependency).
const SwaggerEmbed = () => {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const CSS = 'https://cdn.jsdelivr.net/npm/swagger-ui-dist@5/swagger-ui.css';
    const JS = 'https://cdn.jsdelivr.net/npm/swagger-ui-dist@5/swagger-ui-bundle.js';
    const mount = () => {
      if (cancelled || !ref.current || !window.SwaggerUIBundle) return;
      try {
        ref.current.innerHTML = '';
        window.SwaggerUIBundle({
          url: SPEC_URL, domNode: ref.current, deepLinking: false,
          presets: [window.SwaggerUIBundle.presets.apis], layout: 'BaseLayout',
        });
      } catch (e) { setFailed(true); }
    };
    if (window.SwaggerUIBundle) { mount(); return undefined; }
    if (!document.querySelector(`link[href="${CSS}"]`)) {
      const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CSS; document.head.appendChild(l);
    }
    let s = document.querySelector(`script[src="${JS}"]`);
    if (!s) {
      s = document.createElement('script'); s.src = JS; s.async = true;
      s.onload = mount; s.onerror = () => setFailed(true); document.body.appendChild(s);
    } else { s.addEventListener('load', mount); mount(); }
    return () => { cancelled = true; };
  }, []);
  if (failed) return <p className="text-sm text-cn-text-muted" data-testid="swagger-fallback">Interactive viewer unavailable — use the spec URL above to open it in Swagger Editor.</p>;
  return <div ref={ref} className="bg-white rounded-2xl border border-cn-border overflow-hidden" data-testid="swagger-embed" style={{ minHeight: 200 }} />;
};

export default function DischargeApiDocs() {
  const location = useLocation();
  const initialKey = new URLSearchParams(location.search).get('key') || '';
  const [apiKey, setApiKey] = useState(initialKey);
  const [keyCopied, setKeyCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [running, setRunning] = useState(null);
  const [output, setOutput] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookBusy, setWebhookBusy] = useState(null);
  const [webhookResult, setWebhookResult] = useState(null);

  const copyKey = () => {
    navigator.clipboard?.writeText(apiKey).then(() => { setKeyCopied(true); toast.success('API key copied'); setTimeout(() => setKeyCopied(false), 1400); }).catch(() => {});
  };

  // Generate + download a Postman v2.1 collection for the FHIR surface. The
  // collection embeds the user's current sandbox key so they can import-and-run.
  const downloadPostman = () => {
    try {
      const blob = new Blob(
        [JSON.stringify(POSTMAN_COLLECTION(API, apiKey), null, 2)],
        { type: 'application/json' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'caraloom-fhir-r4.postman_collection.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Postman collection downloaded');
    } catch (e) {
      toast.error('Could not generate the collection');
    }
  };

  const run = async (which) => {
    if (!apiKey.trim()) { toast.error('Paste your sandbox API key first'); return; }
    setRunning(which);
    setOutput(null);
    const headers = { Authorization: `Bearer ${apiKey.trim()}` };
    try {
      let res;
      if (which === 'search') {
        res = await axios.get(`${API}/v1/clinicians/search?state=TX&limit=3`, { headers });
      } else if (which === 'referral') {
        res = await axios.post(`${API}/v1/discharge-referrals`, { primary_diagnosis: 'CHF', care_type: 'Skilled Nursing', zip: '78701' }, { headers });
      } else if (which === 'fhir') {
        // CapabilityStatement is public; the bearer header is harmless if set.
        res = await axios.get(`${API}/v1/fhir/metadata`);
      } else {
        res = await axios.get(`${API}/v1/geo/zip/78701`, { headers });
      }
      setOutput({ ok: true, data: res.data });
    } catch (err) {
      setOutput({ ok: false, data: err.response?.data || { error: 'Request failed' } });
      toast.error(err.response?.data?.detail || 'Request failed — check your key');
    } finally {
      setRunning(null);
    }
  };

  const registerWebhook = async () => {
    if (!apiKey.trim()) { toast.error('Paste your sandbox API key first'); return; }
    if (!/^https?:\/\//.test(webhookUrl.trim())) { toast.error('Enter a valid http(s) URL'); return; }
    setWebhookBusy('register');
    try {
      const { data } = await axios.post(`${API}/v1/webhooks`, { url: webhookUrl.trim(), events: ['referral.created', 'ping'] }, { headers: { Authorization: `Bearer ${apiKey.trim()}` } });
      setWebhookSecret(data.signing_secret || '');
      toast.success('Webhook registered — secret shown once below');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not register webhook');
    } finally {
      setWebhookBusy(null);
    }
  };

  const testWebhook = async () => {
    if (!apiKey.trim()) { toast.error('Paste your sandbox API key first'); return; }
    setWebhookBusy('test');
    setWebhookResult(null);
    try {
      const { data } = await axios.post(`${API}/v1/webhooks/test`, {}, { headers: { Authorization: `Bearer ${apiKey.trim()}` } });
      setWebhookResult(data.delivery || { status: 'sent' });
      toast.success('Test ping sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send test ping');
    } finally {
      setWebhookBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-cn-bg">
      <Seo title="Discharge API — CaraLoom for Developers" description="Build hospital-to-home discharge into your workflow. Search the verified clinical workforce, submit referrals, and resolve geography — with a self-serve sandbox key." path="/developers" />
      <PublicNavbar />

      {/* Hero */}
      <section className="relative overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 60%, #334155 100%)' }}>
        <div className="absolute -top-20 -right-24 w-96 h-96 rounded-full bg-cn-accent/20 blur-3xl" />
        <div className="cn-container relative py-20 md:py-28">
          <div className="inline-flex items-center gap-2 text-[#22D3EE] text-xs font-bold uppercase tracking-wider mb-4">
            <Code2 className="w-4 h-4" /> Discharge API · Sandbox
          </div>
          <h1 className="cn-display text-4xl md:text-6xl leading-tight max-w-3xl">Build hospital-to-home into your workflow.</h1>
          <p className="mt-5 text-white/75 text-lg max-w-2xl leading-relaxed">
            Search the verified clinical workforce, submit one-click discharge referrals, and resolve geography — programmatically. Get a self-serve sandbox key in seconds.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href="#try-it" className="cn-btn cn-btn-accent !py-3 px-7 text-base justify-center" data-testid="docs-try-it-cta">
              Try it live <Zap className="w-4 h-4" />
            </a>
            {!apiKey && (
              <button onClick={() => setModalOpen(true)} className="inline-flex items-center justify-center gap-2 py-3 px-7 text-base font-semibold rounded-xl border border-white/30 text-white hover:bg-white/10 transition-colors" data-testid="docs-request-key">
                Get a sandbox key <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="cn-container py-16 md:py-20 space-y-12">
        {/* Your key */}
        <section data-testid="docs-key-section">
          <div className="cn-card rounded-3xl p-6 md:p-8">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="w-5 h-5 text-cn-primary" />
              <h2 className="cn-display text-2xl text-cn-text">Your sandbox key</h2>
            </div>
            {apiKey ? (
              <>
                <p className="text-sm text-cn-text-muted mb-4">Use this in the <code className="text-cn-primary">Authorization: Bearer</code> header. Sandbox keys are read-mostly and return de-identified demo data.</p>
                <div className="flex items-center gap-2 bg-cn-ink-900 rounded-xl p-3">
                  <code className="text-slate-200 text-sm font-mono flex-1 truncate" data-testid="docs-key-value">{apiKey}</code>
                  <button onClick={copyKey} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white" data-testid="docs-copy-key">
                    {keyCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-cn-text-muted mb-4">Paste an existing sandbox key, or request one — it&apos;s issued instantly to your inbox.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="clk_sandbox_..." className="cn-input flex-1 font-mono text-sm" data-testid="docs-key-input" />
                  <button onClick={() => setModalOpen(true)} className="cn-btn cn-btn-primary justify-center" data-testid="docs-get-key-btn">Get a sandbox key</button>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Endpoint reference */}
        <section>
          <h2 className="cn-display text-2xl text-cn-text mb-1">Endpoints</h2>
          <p className="text-sm text-cn-text-muted mb-5">Base URL <code className="text-cn-primary">{API}</code></p>
          <div className="space-y-3">
            {ENDPOINTS.map((ep) => (
              <div key={ep.path} className="cn-card rounded-2xl p-5" data-testid={`docs-endpoint-${ep.method.toLowerCase()}-${ep.path.replace(/[^a-z]/gi, '')}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${ep.method === 'GET' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{ep.method}</span>
                  <code className="text-sm font-mono text-cn-text">{ep.path}</code>
                  {ep.auth
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-cn-text-muted"><ShieldCheck className="w-3 h-3" /> key required</span>
                    : <span className="text-[10px] font-semibold text-emerald-600">public</span>}
                </div>
                <p className="text-sm text-cn-text-muted mt-2">{ep.desc}</p>
                {ep.params && <p className="text-xs text-cn-text-muted mt-1">Query: <code className="text-cn-primary">{ep.params}</code></p>}
                {ep.body && <p className="text-xs text-cn-text-muted mt-1">Body: <code className="text-cn-primary">{ep.body}</code></p>}
              </div>
            ))}
          </div>
          <div className="mt-5">
            <p className="text-sm font-semibold text-cn-text mb-2">Example</p>
            <CodeBlock testid="docs-curl-example">{`curl "${API}/v1/clinicians/search?state=TX&limit=3" \\
  -H "Authorization: Bearer ${apiKey || 'clk_sandbox_YOUR_KEY'}"`}</CodeBlock>
          </div>
        </section>

        {/* FHIR R4 — Hospital-to-Home interoperability */}
        <section id="fhir" className="scroll-mt-20" data-testid="docs-fhir-section">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5 text-cn-primary" />
            <h2 className="cn-display text-2xl text-cn-text">FHIR R4 — Hospital-to-Home</h2>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">R4</span>
          </div>
          <p className="text-sm text-cn-text-muted mb-5">
            For Epic, Cerner, and integration engines: every endpoint below returns a real
            FHIR R4 resource (proper <code className="text-cn-primary">resourceType</code>,
            US-Core profile reference, <code className="text-cn-primary">Bundle</code>{' '}
            <em>searchset</em> on search, and <code className="text-cn-primary">OperationOutcome</code>{' '}
            on errors). The CapabilityStatement is public; reads/writes use the same
            sandbox API key as the native endpoints above.
          </p>
          <div className="space-y-3">
            {FHIR_ENDPOINTS.map((ep) => (
              <div key={`${ep.method}-${ep.path}`} className="cn-card rounded-2xl p-5" data-testid={`docs-fhir-${ep.method.toLowerCase()}-${ep.path.replace(/[^a-z]/gi, '')}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${ep.method === 'GET' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{ep.method}</span>
                  <code className="text-sm font-mono text-cn-text">{ep.path}</code>
                  {ep.auth
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-cn-text-muted"><ShieldCheck className="w-3 h-3" /> key required</span>
                    : <span className="text-[10px] font-semibold text-emerald-600">public</span>}
                </div>
                <p className="text-sm text-cn-text-muted mt-2">{ep.desc}</p>
                {ep.example && <p className="text-xs text-cn-text-muted mt-1">Example: <code className="text-cn-primary">{ep.example}</code></p>}
              </div>
            ))}
          </div>

          <div className="mt-5">
            <p className="text-sm font-semibold text-cn-text mb-2">Fetch the CapabilityStatement</p>
            <CodeBlock testid="docs-fhir-metadata-example">{`curl "${API}/v1/fhir/metadata" \\
  -H "Accept: application/fhir+json"`}</CodeBlock>
          </div>

          <div className="mt-4">
            <p className="text-sm font-semibold text-cn-text mb-2">Create a discharge referral (FHIR ServiceRequest)</p>
            <CodeBlock testid="docs-fhir-create-sr">{`curl -X POST "${API}/v1/fhir/ServiceRequest" \\
  -H "Authorization: Bearer ${apiKey || 'clk_sandbox_YOUR_KEY'}" \\
  -H "Content-Type: application/fhir+json" \\
  -d '{
    "resourceType": "ServiceRequest",
    "status": "active",
    "intent": "order",
    "subject": { "display": "Jane Doe, MRN-12345" },
    "priority": "urgent",
    "reasonCode": [{
      "coding": [{
        "system": "http://hl7.org/fhir/sid/icd-10-cm",
        "code": "I50.9",
        "display": "Heart failure"
      }]
    }],
    "note": [{ "text": "Needs IV diuretic monitoring at home." }]
  }'`}</CodeBlock>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={downloadPostman}
              className="cn-btn cn-btn-ghost text-sm justify-center"
              data-testid="docs-fhir-postman-btn"
            >
              <Download className="w-4 h-4" /> Download Postman collection
            </button>
            <button
              type="button"
              onClick={() => run('fhir')}
              disabled={running}
              className="cn-btn cn-btn-primary text-sm justify-center"
              data-testid="docs-fhir-run-btn"
            >
              {running === 'fhir' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {' '}Run FHIR CapabilityStatement
            </button>
          </div>
        </section>

        {/* Try it live */}
        <section id="try-it" className="scroll-mt-20" data-testid="docs-tryit-section">
          <div className="flex items-center gap-2 mb-2">
            <Terminal className="w-5 h-5 text-cn-primary" />
            <h2 className="cn-display text-2xl text-cn-text">Try it live</h2>
          </div>
          <p className="text-sm text-cn-text-muted mb-4">Runs against the live sandbox using the key above.</p>
          {!apiKey && (
            <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Paste your sandbox key to run requests" className="cn-input w-full font-mono text-sm mb-3" data-testid="docs-tryit-key-input" />
          )}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => run('search')} disabled={running} className="cn-btn cn-btn-primary !py-2" data-testid="docs-run-search">
              {running === 'search' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Search clinicians (TX)
            </button>
            <button onClick={() => run('referral')} disabled={running} className="cn-btn cn-btn-ghost !py-2" data-testid="docs-run-referral">
              {running === 'referral' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Create referral
            </button>
            <button onClick={() => run('geo')} disabled={running} className="cn-btn cn-btn-ghost !py-2" data-testid="docs-run-geo">
              {running === 'geo' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Resolve ZIP 78701
            </button>
          </div>
          {output && (
            <div className="mt-4">
              <p className={`text-xs font-bold mb-1 ${output.ok ? 'text-emerald-600' : 'text-red-600'}`}>{output.ok ? '200 OK' : 'Error'}</p>
              <CodeBlock testid="docs-output">{JSON.stringify(output.data, null, 2)}</CodeBlock>
            </div>
          )}
        </section>

        {/* Webhooks */}
        <section data-testid="docs-webhooks-section">
          <div className="flex items-center gap-2 mb-2">
            <Webhook className="w-5 h-5 text-cn-primary" />
            <h2 className="cn-display text-2xl text-cn-text">Webhooks</h2>
          </div>
          <p className="text-sm text-cn-text-muted mb-4">
            Register an endpoint to receive signed event callbacks (<code className="text-cn-primary">{WEBHOOK_EVENTS.join(', ')}</code>). Every delivery is signed with HMAC-SHA256 over the raw body in the <code className="text-cn-primary">X-CaraLoom-Signature: sha256=…</code> header — verify it with your signing secret.
          </p>
          <div className="cn-card rounded-2xl p-5">
            <p className="text-xs font-medium text-cn-text mb-1">Your webhook URL</p>
            <div className="flex flex-col sm:flex-row gap-2 mt-1">
              <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://your-app.com/hooks/caraloom" className="cn-input flex-1 font-mono text-sm" data-testid="docs-webhook-url" />
              <button onClick={registerWebhook} disabled={webhookBusy} className="cn-btn cn-btn-primary justify-center !py-2" data-testid="docs-webhook-register">
                {webhookBusy === 'register' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Webhook className="w-4 h-4" />} Register
              </button>
              <button onClick={testWebhook} disabled={webhookBusy} className="cn-btn cn-btn-ghost justify-center !py-2" data-testid="docs-webhook-test">
                {webhookBusy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Send test ping
              </button>
            </div>
            {webhookSecret && (
              <div className="mt-3">
                <p className="text-xs text-cn-text-muted mb-1">Signing secret (shown once — store it):</p>
                <CodeBlock testid="docs-webhook-secret">{webhookSecret}</CodeBlock>
              </div>
            )}
            {webhookResult && (
              <p className="mt-3 text-xs" data-testid="docs-webhook-result">
                Last delivery: <span className={webhookResult.status === 'delivered' ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'}>{webhookResult.status}</span>
                {webhookResult.response_code ? ` (HTTP ${webhookResult.response_code})` : ''}
              </p>
            )}
          </div>
          <div className="mt-4">
            <p className="text-sm font-semibold text-cn-text mb-2">Verify a signature (Python)</p>
            <CodeBlock testid="docs-verify-example">{`import hmac, hashlib
sig = hmac.new(SECRET.encode(), raw_body, hashlib.sha256).hexdigest()
assert request.headers["X-CaraLoom-Signature"] == f"sha256={sig}"`}</CodeBlock>
          </div>
        </section>

        {/* Production tier */}
        <section data-testid="docs-production-section">
          <div className="flex items-center gap-2 mb-2">
            <Gauge className="w-5 h-5 text-cn-primary" />
            <h2 className="cn-display text-2xl text-cn-text">Sandbox vs. production</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="cn-card rounded-2xl p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-cn-text-muted mb-2">Sandbox</p>
              <ul className="text-sm text-cn-text-muted space-y-1.5">
                <li>• De-identified demo data</li>
                <li>• 60 req/min · 5,000 req/day</li>
                <li>• Referrals stored in a sandbox store</li>
                <li>• Self-serve key, instant</li>
              </ul>
            </div>
            <div className="cn-card rounded-2xl p-5 border-cn-primary/30">
              <p className="text-xs font-bold uppercase tracking-wide text-cn-primary mb-2">Production</p>
              <ul className="text-sm text-cn-text-muted space-y-1.5">
                <li>• Real write-through to the live discharge pipeline</li>
                <li>• Per-org configurable rate limits</li>
                <li>• <code className="text-cn-primary">referral.created</code> / <code className="text-cn-primary">referral.matched</code> webhooks</li>
                <li>• Activated by our team — <button onClick={() => setModalOpen(true)} className="text-cn-primary font-semibold underline" data-testid="docs-request-prod">request access</button></li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-cn-text-muted mt-3">Every response includes a <code className="text-cn-primary">rate</code> object with your remaining quota. A <code className="text-cn-primary">429</code> with <code className="text-cn-primary">Retry-After</code> is returned when a window is exhausted.</p>
        </section>

        {/* OpenAPI / Swagger */}
        <section data-testid="docs-openapi-section">
          <div className="flex items-center gap-2 mb-2">
            <FileJson className="w-5 h-5 text-cn-primary" />
            <h2 className="cn-display text-2xl text-cn-text">OpenAPI &amp; Swagger</h2>
          </div>
          <p className="text-sm text-cn-text-muted mb-4">A machine-readable OpenAPI 3.0 spec — import it into Postman, generate a client, or explore it interactively below.</p>
          <div className="flex flex-wrap gap-2 mb-5">
            <a href={SPEC_URL} target="_blank" rel="noreferrer" download="caraloom-discharge-api.json" className="cn-btn cn-btn-primary !py-2" data-testid="docs-download-spec">
              <Download className="w-4 h-4" /> Download openapi.json
            </a>
            <button onClick={() => { navigator.clipboard?.writeText(SPEC_URL); toast.success('Spec URL copied'); }} className="cn-btn cn-btn-ghost !py-2" data-testid="docs-copy-spec-url">
              <Copy className="w-4 h-4" /> Copy spec URL
            </button>
            <a href={`https://editor.swagger.io/?url=${encodeURIComponent(SPEC_URL)}`} target="_blank" rel="noreferrer" className="cn-btn cn-btn-ghost !py-2" data-testid="docs-open-editor">
              <Code2 className="w-4 h-4" /> Open in Swagger Editor
            </a>
          </div>
          <SwaggerEmbed />
        </section>
      </div>

      <Footer />

      <InstitutionInquiryModal
        open={modalOpen}
        defaultInterest="api_access"
        source="developers_page"
        onOpenChange={setModalOpen}
        onIssued={(key) => { setApiKey(key); toast.success('Sandbox key issued — you\'re ready to build.'); }}
      />
    </div>
  );
}
